import { useCallback, useEffect, useState } from 'react';
import AppLayout from '../../components/AppLayout';
import {
  getSafetyConfig,
  updateSafetyConfig,
  listSafetyAcknowledgements,
} from '../../services/safetyLoginService';

const LANGUAGES = [
  { code: 'en', label: 'English' },
  { code: 'es', label: 'Spanish' },
  { code: 'fr', label: 'French' },
  { code: 'pt', label: 'Portuguese' },
  { code: 'ht', label: 'Haitian Creole' },
  { code: 'ar', label: 'Arabic' },
  { code: 'zh', label: 'Chinese' },
  { code: 'ru', label: 'Russian' },
  { code: 'pl', label: 'Polish' },
  { code: 'other', label: 'Other' },
];

const todayStr = () => new Date().toISOString().slice(0, 10);
const daysAgoStr = (n) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().slice(0, 10);
};

const fmtDateTime = (iso) => {
  if (!iso) return '—';
  return new Date(iso).toLocaleString();
};

export default function SafetyLoginMessage({ embedded = false }) {
  const [tab, setTab] = useState('config');

  // ── Config state ─────────────────────────────────
  const [cfg, setCfg] = useState({ enabled: false, title: 'Safety Notice', body: '', language: 'en' });
  const [cfgLoading, setCfgLoading] = useState(true);
  const [cfgSaving, setCfgSaving] = useState(false);
  const [cfgError, setCfgError] = useState('');
  const [cfgSuccess, setCfgSuccess] = useState('');

  // ── Log state ─────────────────────────────────────
  const [logFilters, setLogFilters] = useState({
    from: daysAgoStr(29),
    to: todayStr(),
    driverId: '',
    status: '',
  });
  const [events, setEvents] = useState([]);
  const [logTotal, setLogTotal] = useState(0);
  const [logLoading, setLogLoading] = useState(false);
  const [logError, setLogError] = useState('');

  // ── Load config ───────────────────────────────────
  useEffect(() => {
    let ignore = false;
    setCfgLoading(true);
    getCfg();
    return () => { ignore = true; };
    async function getCfg() {
      try {
        const res = await getSafetyConfig();
        if (!ignore) setCfg(res.data.safetyLoginMessage);
      } catch {
        if (!ignore) setCfgError('Unable to load configuration.');
      } finally {
        if (!ignore) setCfgLoading(false);
      }
    }
  }, []);

  // ── Save config ───────────────────────────────────
  const handleSave = async (e) => {
    e.preventDefault();
    if (cfg.enabled && !cfg.body.trim()) {
      setCfgError('Message body is required when the feature is enabled.');
      return;
    }
    setCfgSaving(true);
    setCfgError('');
    setCfgSuccess('');
    try {
      const res = await updateSafetyConfig(cfg);
      setCfg(res.data.safetyLoginMessage);
      setCfgSuccess('Configuration saved.');
      window.setTimeout(() => setCfgSuccess(''), 4000);
    } catch (err) {
      setCfgError(err.response?.data?.message || 'Failed to save configuration.');
    } finally {
      setCfgSaving(false);
    }
  };

  // ── Load log ──────────────────────────────────────
  const loadLog = useCallback(async () => {
    setLogLoading(true);
    setLogError('');
    try {
      const params = { limit: 500 };
      if (logFilters.from) params.from = logFilters.from;
      if (logFilters.to) params.to = logFilters.to;
      if (logFilters.driverId.trim()) params.driverId = logFilters.driverId.trim();
      if (logFilters.status) params.status = logFilters.status;
      const res = await listSafetyAcknowledgements(params);
      setEvents(res.data.events || []);
      setLogTotal(res.data.total ?? res.data.events?.length ?? 0);
    } catch {
      setLogError('Unable to load acknowledgement log.');
    } finally {
      setLogLoading(false);
    }
  }, [logFilters]);

  useEffect(() => {
    if (tab === 'log') loadLog();
  }, [tab, loadLog]);

  // ── CSV export ────────────────────────────────────
  const exportCsv = () => {
    const headers = ['Driver ID', 'Driver Name', 'Cab #', 'Language', 'Login At', 'Acknowledged At', 'Status'];
    const rows = events.map((ev) => [
      ev.driverId,
      ev.driverName,
      ev.cabNumber,
      ev.messageLanguage,
      ev.loginAt ? new Date(ev.loginAt).toISOString() : '',
      ev.acknowledgedAt ? new Date(ev.acknowledgedAt).toISOString() : '',
      ev.acknowledgedAt ? 'Acknowledged' : 'Pending',
    ]);
    const csv = [headers, ...rows]
      .map((r) => r.map((c) => `"${String(c ?? '').replace(/"/g, '""')}"`).join(','))
      .join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `safety-ack-${logFilters.from}-to-${logFilters.to}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const content = (
    <>
      {/* ── Tab switcher ────────────────────────────── */}
      {!embedded && <div className="map-toggle" style={{ justifyContent: 'flex-start', padding: '0 0 24px' }}>
        <button
          type="button"
          className={`btn btn-ghost ${tab === 'config' ? 'active' : ''}`}
          onClick={() => setTab('config')}
        >
          Configuration
        </button>
        <button
          type="button"
          className={`btn btn-ghost ${tab === 'log' ? 'active' : ''}`}
          onClick={() => setTab('log')}
        >
          Acknowledgement Log
        </button>
      </div>}

      {/* ── CONFIGURATION TAB ────────────────────────── */}
      {tab === 'config' && (
        <div className="panel" style={{ maxWidth: 720 }}>
          {cfgLoading ? (
            <div className="skeleton" style={{ height: 300, borderRadius: 12 }} />
          ) : (
            <form onSubmit={handleSave} className="panel-form">
              <div className="panel-header">
                <div>
                  <h3>Safety login message</h3>
                  <p className="panel-subtitle">
                    When enabled, every driver login triggers a mandatory notice delivered via
                    Socket.IO (instant) and FCM push (background). The driver must acknowledge
                    before continuing. All acknowledgements are recorded in the log.
                  </p>
                </div>
                <div className="panel-actions">
                  <span className={`badge ${cfg.enabled ? 'badge-success' : 'badge-warning'}`}>
                    {cfg.enabled ? 'Active' : 'Inactive'}
                  </span>
                </div>
              </div>

              <div className="panel-body">
                {cfgError && <div className="feedback error">{cfgError}</div>}
                {cfgSuccess && <div className="feedback success">{cfgSuccess}</div>}

                {/* Enable / disable toggle */}
                <div style={{ marginBottom: 20 }}>
                  <label style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
                    <input
                      type="checkbox"
                      checked={cfg.enabled}
                      onChange={(e) => setCfg((p) => ({ ...p, enabled: e.target.checked }))}
                    />
                    <span><strong>Enable Safety Login Message</strong></span>
                  </label>
                  <p className="hint" style={{ marginTop: 4 }}>
                    When disabled, drivers log in normally with no safety prompt. All settings
                    and acknowledgement history are preserved.
                  </p>
                </div>

                <div className="form-grid">
                  <div>
                    <label htmlFor="safetyTitle">Message title</label>
                    <input
                      id="safetyTitle"
                      type="text"
                      maxLength={120}
                      value={cfg.title}
                      onChange={(e) => setCfg((p) => ({ ...p, title: e.target.value }))}
                      placeholder="Safety Notice"
                    />
                  </div>
                  <div>
                    <label htmlFor="safetyLanguage">Message language</label>
                    <select
                      id="safetyLanguage"
                      value={cfg.language}
                      onChange={(e) => setCfg((p) => ({ ...p, language: e.target.value }))}
                    >
                      {LANGUAGES.map((l) => (
                        <option key={l.code} value={l.code}>{l.label}</option>
                      ))}
                    </select>
                    <p className="hint">Label for the language the message is written in.</p>
                  </div>
                </div>

                <div>
                  <label htmlFor="safetyBody">
                    Message body{' '}
                    <span style={{ color: 'var(--text-secondary)', fontWeight: 400 }}>
                      ({cfg.body.length} / 4000)
                    </span>
                  </label>
                  <textarea
                    id="safetyBody"
                    rows={7}
                    maxLength={4000}
                    value={cfg.body}
                    onChange={(e) => setCfg((p) => ({ ...p, body: e.target.value }))}
                    placeholder="Write the safety notice that drivers must read and acknowledge every time they log in…"
                  />
                  <p className="hint">
                    This message is sent via Socket.IO the moment the driver logs in. If the
                    app is in the background or closed, it also fires as an FCM push notification.
                  </p>
                </div>
              </div>

              <div className="panel-footer">
                <div />
                <button type="submit" className="btn btn-primary" disabled={cfgSaving}>
                  {cfgSaving ? 'Saving…' : 'Save configuration'}
                </button>
              </div>
            </form>
          )}
        </div>
      )}

      {/* ── ACKNOWLEDGEMENT LOG TAB ──────────────────── */}
      {tab === 'log' && (
        <div className="panel">
          {/* Filters */}
          <div className="panel-header" style={{ flexWrap: 'wrap', gap: 12 }}>
            <div>
              <h3>Acknowledgement log</h3>
              <p className="panel-subtitle">Every login event where a safety notice was delivered.</p>
            </div>
          </div>

          <div className="panel-body" style={{ paddingBottom: 0 }}>
            <div className="form-grid" style={{ marginBottom: 16 }}>
              <div>
                <label htmlFor="logFrom">From</label>
                <input
                  id="logFrom"
                  type="date"
                  value={logFilters.from}
                  onChange={(e) => setLogFilters((p) => ({ ...p, from: e.target.value }))}
                />
              </div>
              <div>
                <label htmlFor="logTo">To</label>
                <input
                  id="logTo"
                  type="date"
                  value={logFilters.to}
                  onChange={(e) => setLogFilters((p) => ({ ...p, to: e.target.value }))}
                />
              </div>
              <div>
                <label htmlFor="logDriverId">Driver ID</label>
                <input
                  id="logDriverId"
                  type="text"
                  value={logFilters.driverId}
                  placeholder="All drivers"
                  onChange={(e) => setLogFilters((p) => ({ ...p, driverId: e.target.value }))}
                />
              </div>
              <div>
                <label htmlFor="logStatus">Status</label>
                <select
                  id="logStatus"
                  value={logFilters.status}
                  onChange={(e) => setLogFilters((p) => ({ ...p, status: e.target.value }))}
                >
                  <option value="">All</option>
                  <option value="acknowledged">Acknowledged</option>
                  <option value="pending">Pending</option>
                </select>
              </div>
            </div>

            <div className="toolbar" style={{ marginBottom: 12 }}>
              <button className="btn btn-ghost" onClick={loadLog} disabled={logLoading}>
                {logLoading ? 'Loading…' : 'Refresh'}
              </button>
              {events.length > 0 && (
                <button className="btn btn-ghost" onClick={exportCsv}>
                  Export CSV
                </button>
              )}
              {!logLoading && (
                <span style={{ color: 'var(--text-secondary)', fontSize: '0.85rem', marginLeft: 'auto' }}>
                  {logTotal.toLocaleString()} record{logTotal !== 1 ? 's' : ''}
                  {events.length < logTotal ? ` (showing first ${events.length})` : ''}
                </span>
              )}
            </div>

            {logError && <div className="feedback error" style={{ marginBottom: 12 }}>{logError}</div>}

            {logLoading ? (
              <div className="skeleton" style={{ height: 200, borderRadius: 12, marginBottom: 16 }} />
            ) : events.length === 0 ? (
              <div className="fleet-alert-empty">No records match the selected filters.</div>
            ) : (
              <div className="table-scroll">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Driver ID</th>
                      <th>Name</th>
                      <th>Cab #</th>
                      <th>Lang</th>
                      <th>Login at</th>
                      <th>Acknowledged at</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {events.map((ev) => (
                      <tr key={ev._id}>
                        <td>
                          <span style={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>
                            {ev.driverId}
                          </span>
                        </td>
                        <td>{ev.driverName || '—'}</td>
                        <td>{ev.cabNumber || '—'}</td>
                        <td style={{ textTransform: 'uppercase', fontSize: '0.8rem' }}>
                          {ev.messageLanguage || 'EN'}
                        </td>
                        <td style={{ whiteSpace: 'nowrap', fontSize: '0.85rem' }}>
                          {fmtDateTime(ev.loginAt)}
                        </td>
                        <td style={{ whiteSpace: 'nowrap', fontSize: '0.85rem' }}>
                          {fmtDateTime(ev.acknowledgedAt)}
                        </td>
                        <td>
                          <span className={`badge ${ev.acknowledgedAt ? 'badge-success' : 'badge-warning'}`}>
                            {ev.acknowledgedAt ? 'Acknowledged' : 'Pending'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );

  if (embedded) return content;

  return (
    <AppLayout
      title="Safety Login Message"
      subtitle="Require drivers to acknowledge a safety notice on every login."
    >
      {content}
    </AppLayout>
  );
}
